// @flow
import React, { PureComponent, type Node, createRef } from "react";
import { noop } from "lodash";
import mediator from "@tvg/mediator";
import type { QuickLink as QuickLinkType } from "@tvg/types/Links";
import type { BinaryFn } from "@tvg/types/Functional";
import {
  Container,
  LinksWrapper,
  QuickLink,
  ExternalQuickLink
} from "./styled-components";

type FDRLink = QuickLinkType & { imageURL: string, label: string };

type Props = {
  quickLinks: FDRLink[],
  onLinkClick: BinaryFn<QuickLinkType, number, void>,
  isInApp: boolean,
  enableNewDerbyDesign: boolean,
  bigSpace: boolean
};

export default class QuickLinks extends PureComponent<Props> {
  observer: IntersectionObserver | null = null;

  bannerRef = [];

  constructor(props: Props) {
    super(props);
    this.bannerRef = this.props.quickLinks.map(() => createRef());
  }

  static defaultProps = {
    quickLinks: [],
    onLinkClick: noop,
    isInApp: false,
    enableNewDerbyDesign: false,
    bigSpace: false
  };

  componentDidMount() {
    this.observer = new IntersectionObserver(this.handleIntersection, {
      root: null,
      rootMargin: "0px",
      threshold: 0.2
    });
    this.bannerRef.forEach((ref) => {
      if (ref.current) {
        this.observer && this.observer.observe(ref.current);
      }
    });
  }

  componentWillUnmount() {
    this.bannerRef.forEach((ref) => {
      if (ref.current) {
        this.observer && this.observer.unobserve(ref.current);
      }
    });
  }

  handleIntersection = (entries: IntersectionObserverEntry[]) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        const { target } = entry;
        const index = this.bannerRef.findIndex((ref) =>
          Object.is(ref.current, target)
        );
        const foundQuickLink = this.props.quickLinks[index];
        mediator.base.dispatch({
          type: "BANNER_CAROUSEL_VIEWED",
          payload: {
            bannerType: (foundQuickLink?.url || "").includes("/tutorials")
              ? "tutorial"
              : "promo",
            bannerName: foundQuickLink?.label || "",
            bannerId: `SGW-${(foundQuickLink?.label || "").replace(" ", "-")}`
          }
        });
      }
    });
  };

  handleQuickLinkClick = (link: QuickLinkType, index: number, event: Event) => {
    this.props.onLinkClick(link, index);
    if (this.props.isInApp && link.onClick) {
      link.onClick(link, event);
    }
  };

  renderQuickLink = (link: FDRLink, index: number, image: Node) =>
    link.newTab ? (
      <ExternalQuickLink
        enableNewDerbyDesign={this.props.enableNewDerbyDesign}
        href={link.url}
        target="_blank"
        data-qa-label="quickLinkExpanded"
        onClick={(event: Event) =>
          this.handleQuickLinkClick(link, index, event)
        }
      >
        {image}
      </ExternalQuickLink>
    ) : (
      <QuickLink
        enableNewDerbyDesign={this.props.enableNewDerbyDesign}
        to={link.url}
        data-qa-label="quickLinkExpanded"
        onClick={(event: Event) =>
          this.handleQuickLinkClick(link, index, event)
        }
      >
        {image}
      </QuickLink>
    );

  renderLinks = () =>
    this.props.quickLinks.map((link: FDRLink, index: number) => (
      <li
        ref={this.bannerRef[index]}
        data-qa-label={`quickLink_${link.id}`}
        key={link.id}
      >
        {this.renderQuickLink(
          link,
          index,
          <img
            src={link.imageURL}
            data-qa-label="quickLinkExpandedImg"
            alt={link.label}
          />
        )}
      </li>
    ));

  render = () => (
    <Container
      data-qa-label="quickLinksContainer"
      enableNewDerbyDesign={this.props.enableNewDerbyDesign}
    >
      <LinksWrapper
        enableNewDerbyDesign={this.props.enableNewDerbyDesign}
        bigSpace={this.props.bigSpace}
        data-qa-label="quickLinkList"
      >
        {this.renderLinks()}
      </LinksWrapper>
    </Container>
  );
}
